import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { parseData, getBase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const recordsCommand = new Command('records')
  .description('Manage records');

recordsCommand.command('upsert <record_type>')
  .description('Create or update a record')
  .requiredOption('--data <json>', 'Record data (inline JSON or @filename)')
  .option('--id <id>', 'Internal record ID (UUID) to update a known record')
  .option('--external-id <id>', 'External/agent-supplied ID for idempotent upsert')
  .option('--external-source <source>', 'Source system for external_id (e.g. stripe)')
  .action(async function (this: Command, record_type: string, opts: { data: string; id?: string; externalId?: string; externalSource?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const body: Record<string, unknown> = { data: parseData(opts.data) };
    if (opts.externalId) body['external_id'] = opts.externalId;
    if (opts.externalSource) body['external_source'] = opts.externalSource;
    const path = opts.id
      ? `/v1/${ws}/records/${record_type}/${opts.id}`
      : `/v1/${ws}/records/${record_type}`;
    const data = await client.request('PUT', path, body);
    console.log(formatOutput(data, getFormat(this)));
  });

recordsCommand.command('query <record_type>')
  .description('List/search records: exact filters + fuzzy `search`, sorting, pagination')
  .option('--external-source <source>', 'List from an external source (proxy-backed record_type). Combine with --filter when the source forwards filters.')
  .option('--filter <json>', 'Filter DSL expression (inline JSON or @filename). Use the `search` operator for fuzzy matching.')
  .option('--sort <json>', 'Sort expressions (inline JSON or @filename), e.g. [{"field":"data.created","direction":"desc"}]')
  .option('--fields <list>', 'Comma-separated field projection')
  .option('--limit <n>', 'Max results')
  .option('--offset <n>', 'Pagination offset')
  .action(async function (this: Command, record_type: string, opts: { externalSource?: string; filter?: string; sort?: string; fields?: string; limit?: string; offset?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const qs = new URLSearchParams();
    if (opts.externalSource) qs.set('external_source', opts.externalSource);
    if (opts.filter) qs.set('filter', JSON.stringify(parseData(opts.filter)));
    if (opts.sort) qs.set('sort', JSON.stringify(parseData(opts.sort)));
    if (opts.fields) qs.set('fields', opts.fields);
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    const q = qs.toString();
    const data = await client.request('GET', `/v1/${ws}/records/${record_type}${q ? `?${q}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });

recordsCommand.command('get <record_type> <id>')
  .description('Get a record by internal ID, or by external_id (pass --external-source to scope it)')
  .option('--external-source <source>', 'Treat <id> as an external_id scoped to this source system (e.g. stripe)')
  .action(async function (this: Command, record_type: string, id: string, opts: { externalSource?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    // <id> may be an internal UUID or an external_id; encode it for the path segment.
    let path = `/v1/${ws}/records/${record_type}/${encodeURIComponent(id)}`;
    if (opts.externalSource) path += `?external_source=${encodeURIComponent(opts.externalSource)}`;
    const data = await client.request('GET', path);
    console.log(formatOutput(data, getFormat(this)));
  });

recordsCommand.command('delete <record_type> <id>')
  .description('Delete a record by internal ID, or by external_id (pass --external-source to scope it)')
  .option('-y, --yes', 'Skip confirmation prompt')
  .option('--external-source <source>', 'Treat <id> as an external_id scoped to this source system (e.g. stripe)')
  .action(async function (this: Command, record_type: string, id: string, opts: { yes?: boolean; externalSource?: string }) {
    if (!opts.yes && !(await confirm(`Delete record ${record_type}/${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    let path = `/v1/${ws}/records/${record_type}/${encodeURIComponent(id)}`;
    // With a source, <id> is an external_id: forward both query params (scope + external_id)
    // so the backend resolves the native record by its external identity.
    if (opts.externalSource) {
      path += `?external_source=${encodeURIComponent(opts.externalSource)}&external_id=${encodeURIComponent(id)}`;
    }
    await client.request('DELETE', path);
    console.log('Deleted');
  });

recordsCommand.command('history <id>')
  .description('View the change history (audit log) for a record — every version, who changed it, and when')
  .option('--limit <n>', 'Max results')
  .option('--offset <n>', 'Pagination offset')
  .option('--diff', 'Include field-level diffs between versions')
  .action(async function (this: Command, id: string, opts: { limit?: string; offset?: string; diff?: boolean }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    if (opts.diff) qs.set('diff', 'true');
    const q = qs.toString();
    const data = await client.request('GET', `/v1/${ws}/audit/records/${id}${q ? `?${q}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });
